// ============================================================
// Board — State creation, cloning, queries
// ============================================================

import {
  type BoardState, type Grid, type Cell, type Piece, type Side,
  type Position, type CastlingRights,
  NUM_PLANES, BOARD_SIZE,
} from './types';

// --------------- Factory ---------------

let pieceIdCounter = 0;

function makePiece(kind: Piece['kind'], side: Side): Piece {
  return {
    kind,
    side,
    id: `${side[0]}${kind[0]}${pieceIdCounter++}`,
    moveCount: 0,
    lastMovedTurn: -1,
    crossDimCooldown: 0,
  };
}

/** Create an empty 4D grid */
export function emptyGrid(): Grid {
  return Array.from({ length: NUM_PLANES }, () =>
    Array.from({ length: BOARD_SIZE }, () =>
      Array.from({ length: BOARD_SIZE }, () => null)
    )
  );
}

/** Place a piece on the grid (mutating — use only during init) */
function place(grid: Grid, x: number, y: number, w: number, kind: Piece['kind'], side: Side): void {
  grid[w][y][x] = makePiece(kind, side);
}

/** Create the initial board state with pieces distributed across planes */
export function createInitialState(): BoardState {
  pieceIdCounter = 0;
  const grid = emptyGrid();

  // === W=0 EARTH — Full standard chess setup ===
  const backRank: Piece['kind'][] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'];
  for (let x = 0; x < 8; x++) {
    place(grid, x, 0, 0, backRank[x], 'white');
    place(grid, x, 1, 0, 'pawn', 'white');
    place(grid, x, 6, 0, 'pawn', 'black');
    place(grid, x, 7, 0, backRank[x], 'black');
  }

  // === W=1 AETHER — Scouts (bishops + knights) ===
  place(grid, 1, 0, 1, 'knight', 'white');
  place(grid, 2, 0, 1, 'bishop', 'white');
  place(grid, 5, 0, 1, 'bishop', 'white');
  place(grid, 6, 0, 1, 'knight', 'white');
  place(grid, 1, 7, 1, 'knight', 'black');
  place(grid, 2, 7, 1, 'bishop', 'black');
  place(grid, 5, 7, 1, 'bishop', 'black');
  place(grid, 6, 7, 1, 'knight', 'black');

  // === W=2 VOID — Anchors (rooks + pawns) ===
  place(grid, 0, 0, 2, 'rook', 'white');
  place(grid, 7, 0, 2, 'rook', 'white');
  place(grid, 3, 3, 2, 'pawn', 'white');
  place(grid, 4, 3, 2, 'pawn', 'white');
  place(grid, 0, 7, 2, 'rook', 'black');
  place(grid, 7, 7, 2, 'rook', 'black');
  place(grid, 3, 4, 2, 'pawn', 'black');
  place(grid, 4, 4, 2, 'pawn', 'black');

  // === W=3 FLUX — Sovereigns (queens) ===
  place(grid, 3, 3, 3, 'queen', 'white');
  place(grid, 4, 4, 3, 'queen', 'black');

  const castling: CastlingRights = {
    white: { kingSide: true, queenSide: true },
    black: { kingSide: true, queenSide: true },
  };

  return {
    grid,
    turn: 1,
    activePlayer: 'white',
    castlingRights: castling,
    enPassantTarget: null,
    halfMoveClock: 0,
    hash: '', // TODO: Zobrist hash
  };
}

// --------------- Queries ---------------

/** Get piece at position */
export function getAt(state: BoardState, p: Position): Cell {
  return state.grid[p.w][p.y][p.x];
}

/** Check if position is within bounds */
export function inBounds(p: Position): boolean {
  return p.x >= 0 && p.x < BOARD_SIZE
      && p.y >= 0 && p.y < BOARD_SIZE
      && p.w >= 0 && p.w < NUM_PLANES;
}

/** Find the king's position for a given side */
export function findKing(state: BoardState, side: Side): Position | null {
  for (let w = 0; w < NUM_PLANES; w++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const cell = state.grid[w][y][x];
        if (cell && cell.kind === 'king' && cell.side === side) {
          return { x, y, w };
        }
      }
    }
  }
  return null;
}

/** Get all pieces for a side with their positions */
export function getPieces(state: BoardState, side: Side): Array<{ piece: Piece; pos: Position }> {
  const result: Array<{ piece: Piece; pos: Position }> = [];
  for (let w = 0; w < NUM_PLANES; w++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const cell = state.grid[w][y][x];
        if (cell && cell.side === side) {
          result.push({ piece: cell, pos: { x, y, w } });
        }
      }
    }
  }
  return result;
}

// --------------- Hashing ---------------

/** Compute a simple string hash of the board position (for repetition detection) */
export function computePositionHash(state: BoardState): string {
  const parts: string[] = [];
  for (let w = 0; w < NUM_PLANES; w++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const cell = state.grid[w][y][x];
        if (cell) {
          parts.push(`${w}${y}${x}${cell.side[0]}${cell.kind[0]}`);
        }
      }
    }
  }
  parts.push(state.activePlayer[0]);
  const cr = state.castlingRights;
  parts.push(`${+cr.white.kingSide}${+cr.white.queenSide}${+cr.black.kingSide}${+cr.black.queenSide}`);
  if (state.enPassantTarget) {
    parts.push(`ep${state.enPassantTarget.x}${state.enPassantTarget.y}${state.enPassantTarget.w}`);
  }
  return parts.join('|');
}

// --------------- Cloning ---------------

/** Deep clone the grid */
export function cloneGrid(grid: Grid): Grid {
  return grid.map(plane =>
    plane.map(rank =>
      rank.map(cell => cell ? { ...cell } : null)
    )
  );
}

/** Clone the entire board state */
export function cloneState(state: BoardState): BoardState {
  return {
    ...state,
    grid: cloneGrid(state.grid),
    castlingRights: {
      white: { ...state.castlingRights.white },
      black: { ...state.castlingRights.black },
    },
    enPassantTarget: state.enPassantTarget ? { ...state.enPassantTarget } : null,
  };
}
